import { Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import Day from 'react-datepicker/dist/day';

interface DateFieldProps {
  label: string;
  name: string;
  value: Date | string | null;
  onChange: (value: string | null) => void;
  isDisabled?: boolean;
  minDate?: string | Dayjs; // Optional minimum date in DD/MM/YYYY
  maxDate?: string | Dayjs; // Optional maximum date in DD/MM/YYYY
  isMandatory?: boolean;
}

const DateField: React.FC<DateFieldProps> = ({
  label,
  name,
  value,
  onChange,
  isDisabled = false,
  minDate,
  maxDate,
  isMandatory = false,
}) => {
  const [isInvalid, setIsInvalid] = useState(false);
  console.log(maxDate, minDate, 'maxDate, minDate');

  // Parse value into Dayjs object
  const parseInputValue = () => {
    if (!value) return null;

    if (value instanceof Date) {
      return dayjs(value);
    }

    if (typeof value === 'string') {
      return dayjs(value, 'DD/MM/YYYY');
    }

    return null;
  };

  const minLimit = minDate && dayjs(minDate, 'DD/MM/YYYY').isValid() ? dayjs(minDate, 'DD/MM/YYYY') : null;

  const maxLimit = maxDate && dayjs(maxDate, 'DD/MM/YYYY').isValid() ? dayjs(maxDate, 'DD/MM/YYYY') : null;

  return (
    <div className="flex flex-col w-full">
      <Typography variant="body2" fontWeight={600} color="textPrimary">
        {label}
        {isMandatory && <span style={{ color: 'red' }}> *</span>}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={parseInputValue()}
          onChange={(newValue) => {
            if (!newValue) {
              setIsInvalid(false);
              onChange(null);
              return;
            }

            const parsed = dayjs(newValue);
            let valid = parsed.isValid();

            if (valid && minLimit) {
              valid = valid && parsed.isAfter(minLimit.subtract(1, 'day'));
            }

            if (valid && maxLimit) {
              valid = valid && parsed.isBefore(maxLimit.add(1, 'day'));
            }

            if (valid) {
              setIsInvalid(false);
              onChange(parsed.format('DD/MM/YYYY'));
            } else {
              setIsInvalid(true);
              onChange(null);
            }
          }}
          format="DD/MM/YYYY"
          disabled={isDisabled}
          minDate={minDate ? dayjs(minDate, 'DD/MM/YYYY') : undefined}
          maxDate={maxDate ? dayjs(maxDate, 'DD/MM/YYYY') : undefined}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              error: isInvalid,
              helperText: isInvalid
                ? `Date must be${minLimit ? ` after ${minLimit.format('DD/MM/YYYY')}` : ''}${
                    minLimit && maxLimit ? ' and' : ''
                  }${maxLimit ? ` before ${maxLimit.format('DD/MM/YYYY')}` : ''}`
                : '',
            },
          }}
          sx={(theme) => ({
            width: '100%',
            marginTop: '8px',
            '& .MuiOutlinedInput-root': {
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 400,
              borderRadius: '8px',
              borderWidth: '1px',
              borderColor: theme.palette.action.selected,
            },
            '& .MuiInputBase-input': {
              padding: '0px',
              borderWidth: '0px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSvgIcon-root': {
              color: theme.palette.primary.main,
            },
          })}
          className={`mt-2 w-full ${isDisabled ? 'bg-gray-200 cursor-not-allowed' : ''}`}
        />
      </LocalizationProvider>
    </div>
  );
};

export default DateField;
